// src/pages/StudentDashboard.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { collection, query, onSnapshot, orderBy } from "firebase/firestore";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import { LOGO_SRC } from "../images";
import StudentPuzzles from "./StudentPuzzles";
import SquareTraining from "./SquareTraining";
import WritingPractice from "./WritingPractice";
import RatingCard from "./RatingCard";
import "./StudentDashboard.css";

const CHESS_PIECES = ["♟","♞","♝","♜","♛","♚","♙","♘","♗","♖","♕","♔"];

// Lightweight 3D mouse-tilt for the hub cards. Pure presentation — applies
// an inline transform on hover and clears it on leave. Respects reduced motion.
function useTilt() {
  const ref = useRef(null);
  function onMouseMove(e) {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transition = "transform 0.08s ease-out";
    el.style.transform = `perspective(900px) rotateY(${px * 14}deg) rotateX(${-py * 14}deg) translateY(-14px) scale(1.05)`;
  }
  function onMouseLeave() {
    const el = ref.current;
    if (!el) return;
    el.style.transition = "";
    el.style.transform = "";
  }
  return { ref, onMouseMove, onMouseLeave };
}

function FloatingPieces() {
  const pieces = useMemo(() => Array.from({ length: 20 }, (_, i) => ({
    id: i,
    piece: CHESS_PIECES[i % CHESS_PIECES.length],
    bottom: Math.random() * 100,
    size: 18 + Math.random() * 28,
    duration: 20 + Math.random() * 20,
    delay: Math.random() * 20,
    opacity: 0.05 + Math.random() * 0.07,
  })), []);

  return (
    <div className="floating-pieces-bg" aria-hidden="true">
      {pieces.map(p => (
        <span key={p.id} className="floating-piece" style={{
          bottom: `${p.bottom}%`,
          fontSize: `${p.size}px`,
          opacity: p.opacity,
          animationDuration: `${p.duration}s`,
          animationDelay: `-${p.delay}s`,
        }}>{p.piece}</span>
      ))}
    </div>
  );
}

export default function StudentDashboard() {
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("home");
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [puzzleSets, setPuzzleSets] = useState([]);
  const [puzzleProgress, setPuzzleProgress] = useState({});
  const classesTilt = useTilt();
  const puzzlesTilt = useTilt();
  const squareTilt = useTilt();
  const writingTilt = useTilt();
  const ratingTilt = useTilt();

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "classes"), orderBy("datetime", "asc"));
    const unsubscribe = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setClasses(all.filter(cls => cls.studentIds?.includes(user.uid)));
      setLoading(false);
    });
    // Also fetch puzzle sets
    const pUnsub = onSnapshot(collection(db, "puzzleSets"), (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const mine = all.filter(s => s.studentIds?.includes(user.uid));
      setPuzzleSets(mine);
      const prog = {};
      mine.forEach(s => { if (s.attempts?.[user.uid]) prog[s.id] = s.attempts[user.uid]; });
      setPuzzleProgress(prog);
    });
    return () => { unsubscribe(); pUnsub(); };
  }, [user]);

  function formatDatetime(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleString("en-IN", {
      weekday: "long", day: "numeric", month: "long",
      year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  }

  function getStatus(cls) {
    if (cls.status === "live") return "live";
    if (cls.status === "ended") return "ended";
    return "scheduled";
  }

  function getCountdown(iso) {
    const diff = new Date(iso) - new Date();
    if (diff <= 0) return "Starting soon";
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (d > 0) return `in ${d}d ${h}h`;
    if (h > 0) return `in ${h}h ${m}m`;
    return `in ${m} min`;
  }

  const liveCount = classes.filter(c => c.status === "live").length;
  const upcomingClass = classes.find(c => c.status === "scheduled");
  const incompletePuzzleSets = puzzleSets.filter(s => {
    const prog = puzzleProgress[s.id] || {};
    const attempted = Object.keys(prog).length;
    return attempted < s.totalPuzzles;
  });
  const totalPuzzlesLeft = incompletePuzzleSets.reduce((sum, s) => {
    const prog = puzzleProgress[s.id] || {};
    return sum + (s.totalPuzzles - Object.keys(prog).length);
  }, 0);
  const newPuzzleSets = incompletePuzzleSets.filter(s => !puzzleProgress[s.id]);

  return (
    <div className="student-layout">
      <header className="student-header">
        <div className="student-header-left">
          <img src={LOGO_SRC} alt="ChessingIt" style={{ height: 44, width: "auto", objectFit: "contain" }} />
          <div>
            <h1>ChessingIt</h1>
            <p>Welcome back, {userData?.name || "Student"}</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {tab !== "home" && (
            <button className="btn btn-secondary" onClick={() => setTab("home")}>← Home</button>
          )}
          <button className="btn btn-secondary" onClick={() => signOut(auth)}>Logout</button>
        </div>
      </header>

      <main className="student-main">

        {/* HOME */}
        {tab === "home" && (
          <div className="home-hub">
            <FloatingPieces />
            
            <div className="hub-cards">

              {/* Classes */}
              <div className="hub-card-wrap hub-enter" style={{ animationDelay: "0.1s" }}>
              <div
                ref={classesTilt.ref}
                onMouseMove={classesTilt.onMouseMove}
                onMouseLeave={classesTilt.onMouseLeave}
                className="hub-card classes-card"
                onClick={() => setTab("classes")}
              >
                <div className="hub-card-glow" />
                {liveCount > 0 && (
                  <div className="card-notif classes-notif">
                    <span className="notif-pulse-ring" />
                    <span className="notif-emoji">🔴</span>
                    <span className="notif-text">{liveCount} LIVE NOW</span>
                  </div>
                )}
                <div className="hub-icon-wrap">
                  <div className="anim-scene">
                  {/* Elegant pawn SVG — hops in from the left on hover */}
                  <svg className="pawn-fly" viewBox="0 0 80 100" xmlns="http://www.w3.org/2000/svg" width="58" height="72">
                    <defs>
                      <radialGradient id="pawnGrad" cx="40%" cy="30%" r="60%">
                        <stop offset="0%" stopColor="#FFE44D"/>
                        <stop offset="100%" stopColor="#FFD700"/>
                      </radialGradient>
                      <filter id="pawnShadow">
                        <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#FFD700" floodOpacity="0.6"/>
                      </filter>
                    </defs>
                    <g filter="url(#pawnShadow)">
                      {/* Head */}
                      <circle cx="40" cy="22" r="16" fill="url(#pawnGrad)"/>
                      {/* Neck */}
                      <path d="M32 36 Q40 40 48 36 L50 48 Q40 52 30 48 Z" fill="url(#pawnGrad)"/>
                      {/* Body */}
                      <path d="M26 48 Q18 58 20 70 L60 70 Q62 58 54 48 Q40 52 26 48 Z" fill="url(#pawnGrad)"/>
                      {/* Base */}
                      <rect x="14" y="70" width="52" height="9" rx="4" fill="#FFD700"/>
                      <rect x="10" y="78" width="60" height="8" rx="4" fill="#FFC200"/>
                    </g>
                  </svg>
                  </div>
                </div>
                <div className="hub-card-text">
                  <div className="hub-card-title">Classes</div>
                  <div className="hub-card-sub">Your scheduled lessons</div>
                </div>
              </div>
              </div>

              {/* Puzzles */}
              <div className="hub-card-wrap hub-enter" style={{ animationDelay: "0.22s" }}>
              <div
                ref={puzzlesTilt.ref}
                onMouseMove={puzzlesTilt.onMouseMove}
                onMouseLeave={puzzlesTilt.onMouseLeave}
                className="hub-card puzzles-card"
                onClick={() => setTab("puzzles")}
              >
                <div className="hub-card-glow" />
                {totalPuzzlesLeft > 0 && (
                  <div className="card-notif puzzles-notif">
                    <span className="notif-emoji">🧩</span>
                    <span className="notif-text">{totalPuzzlesLeft} to solve</span>
                  </div>
                )}
                <div className="hub-icon-wrap">
                  <div className="anim-scene">
                  {/* Four jigsaw pieces — fly in from the corners and join on hover */}
                  <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" width="82" height="82">
                    <path className="pz pz-tl" d="M20 20 L50 20 L50 30 A7 7 0 1 1 50 42 L50 50 L42 50 A7 7 0 0 0 30 50 L20 50 Z" fill="#b9b9ea" stroke="#0a0a0f" strokeWidth="1.4"/>
                    <path className="pz pz-tr" d="M50 20 L80 20 L80 50 L70 50 A7 7 0 1 1 58 50 L50 50 L50 42 A7 7 0 0 0 50 30 Z" fill="#a3a3dd" stroke="#0a0a0f" strokeWidth="1.4"/>
                    <path className="pz pz-bl" d="M20 50 L30 50 A7 7 0 1 1 42 50 L50 50 L50 58 A7 7 0 0 0 50 70 L50 80 L20 80 Z" fill="#9d9dd6" stroke="#0a0a0f" strokeWidth="1.4"/>
                    <path className="pz pz-br" d="M50 50 L58 50 A7 7 0 0 0 70 50 L80 50 L80 80 L50 80 L50 70 A7 7 0 0 1 50 58 Z" fill="#c6c6f2" stroke="#0a0a0f" strokeWidth="1.4"/>
                  </svg>
                  </div>
                </div>
                <div className="hub-card-text">
                  <div className="hub-card-title">Puzzles</div>
                  <div className="hub-card-sub">Chess challenges await</div>
                </div>
              </div>
              </div>

              {/* Square Training */}
              <div className="hub-card-wrap hub-enter" style={{ animationDelay: "0.34s" }}>
              <div
                ref={squareTilt.ref}
                onMouseMove={squareTilt.onMouseMove}
                onMouseLeave={squareTilt.onMouseLeave}
                className="hub-card square-card"
                onClick={() => setTab("square")}
              >
                <div className="hub-card-glow" />
                <div className="hub-icon-wrap">
                  <div className="anim-scene">
                  {/* Arrow flies in and strikes the target on hover */}
                  <svg viewBox="0 0 110 88" xmlns="http://www.w3.org/2000/svg" width="100" height="80">
                    <g className="target-g">
                      <circle cx="44" cy="46" r="31" fill="#0d2f1c" stroke="#2ED573" strokeWidth="2.5"/>
                      <circle cx="44" cy="46" r="22" fill="#10241a" stroke="#2ED573" strokeWidth="1.6" opacity=".8"/>
                      <circle cx="44" cy="46" r="14" fill="#2ED573" opacity=".85"/>
                      <circle cx="44" cy="46" r="7" fill="#eaffe9"/>
                      <circle cx="44" cy="46" r="3" fill="#FFD700"/>
                    </g>
                    <circle className="impact" cx="44" cy="46" r="14" fill="none" stroke="#FFD700" strokeWidth="2.5"/>
                    <g className="arrow-fly">
                      <line x1="44" y1="46" x2="86" y2="12" stroke="#e9dcc0" strokeWidth="3" strokeLinecap="round"/>
                      <polygon points="44,46 52,44 50,52" fill="#FFD700"/>
                      <path d="M86 12 L94 4 M86 12 L96 12 M86 12 L86 2" stroke="#2ED573" strokeWidth="3" strokeLinecap="round"/>
                    </g>
                  </svg>
                  </div>
                </div>
                <div className="hub-card-text">
                  <div className="hub-card-title">Square Training</div>
                  <div className="hub-card-sub">Name that square — fast!</div>
                </div>
              </div>
              </div>

              {/* Writing Practice */}
              <div className="hub-card-wrap hub-enter" style={{ animationDelay: "0.46s" }}>
              <div
                ref={writingTilt.ref}
                onMouseMove={writingTilt.onMouseMove}
                onMouseLeave={writingTilt.onMouseLeave}
                className="hub-card writing-card"
                onClick={() => setTab("writing")}
              >
                <div className="hub-card-glow" />
                <div className="hub-icon-wrap">
                  <div className="anim-scene" style={{ flexDirection: "column", gap: 0 }}>
                  {/* The hand writes on hover */}
                  <span className="write-hand">✍️</span>
                  <svg viewBox="0 0 110 20" xmlns="http://www.w3.org/2000/svg" width="96" height="17" style={{ marginTop: -6 }}>
                    <path className="scribble" d="M6 12 Q16 2 26 12 T46 12 T66 12 T86 12 T104 10" fill="none" stroke="#FFA502" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                  </div>
                </div>
                <div className="hub-card-text">
                  <div className="hub-card-title">Writing Practice</div>
                  <div className="hub-card-sub">Master chess notation</div>
                </div>
              </div>
              </div>

              {/* My Rating */}
              <div className="hub-card-wrap hub-enter" style={{ animationDelay: "0.58s" }}>
              <div
                ref={ratingTilt.ref}
                onMouseMove={ratingTilt.onMouseMove}
                onMouseLeave={ratingTilt.onMouseLeave}
                className="hub-card rating-card-hub"
                onClick={() => setTab("rating")}
              >
                <div className="hub-card-glow" />
                <div className="hub-icon-wrap">
                  <div className="anim-scene">
                  {/* The growth line draws itself on hover */}
                  <svg viewBox="0 0 100 80" xmlns="http://www.w3.org/2000/svg" width="92" height="74">
                    <line x1="10" y1="8" x2="10" y2="68" stroke="#22334d" strokeWidth="2"/>
                    <line x1="10" y1="68" x2="94" y2="68" stroke="#22334d" strokeWidth="2"/>
                    <polyline className="rt-line" points="14,60 32,46 48,52 66,28 88,14" fill="none" stroke="#4a9eff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle className="rt-dot" cx="88" cy="14" r="4.5" fill="#8cc2ff" stroke="#0d0f14" strokeWidth="1.5"/>
                    <path className="rt-dot" d="M88 6 L84 12 L92 12 Z" fill="#2ED573"/>
                  </svg>
                  </div>
                </div>
                <div className="hub-card-text">
                  <div className="hub-card-title">My Rating</div>
                  <div className="hub-card-sub">Track your journey</div>
                </div>
              </div>
              </div>

            </div>
          </div>
        )}

        {/* CLASSES */}
        <div style={{ display: tab === "classes" ? "block" : "none" }}>
          <h2 className="student-section-title">Your Classes</h2>
          {loading && <div className="loading-screen" style={{ minHeight: 200 }}><div className="spinner"/></div>}
          {!loading && classes.length === 0 && (
            <div className="student-empty"><div>📅</div><h3>No classes yet</h3><p>Your teacher hasn't scheduled any classes yet.</p></div>
          )}
          <div className="student-classes-grid">
            {classes.map(cls => {
              const status = getStatus(cls);
              return (
                <div key={cls.id} className={`student-class-card ${status}`}>
                  {status === "live" && <div className="live-pulse" />}
                  <div className="student-class-top">
                    {status === "live" && <span className="tag tag-live">● LIVE NOW</span>}
                    {status === "scheduled" && <span className="tag tag-scheduled">Scheduled</span>}
                    {status === "ended" && <span className="tag tag-done">Ended</span>}
                  </div>
                  <h3 className="student-class-title">{cls.title}</h3>
                  <p className="student-class-time">🕐 {formatDatetime(cls.datetime)}</p>
                  {status === "scheduled" && <p className="student-class-countdown">{getCountdown(cls.datetime)}</p>}
                  {status === "live" && (
                    <button className="btn btn-primary join-btn" onClick={() => navigate(`/class/${cls.id}`)}>Join Class →</button>
                  )}
                  {status === "ended" && <p className="ended-label">Class has ended</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* PUZZLES */}
        <div style={{ display: tab === "puzzles" ? "block" : "none" }}>
          <StudentPuzzles />
        </div>

        {/* SQUARE TRAINING — mounted only when open, so the board sizes correctly */}
        {tab === "square" && <SquareTraining />}

        {/* WRITING PRACTICE — mounted only when open, so the board sizes correctly */}
        {tab === "writing" && <WritingPractice />}

        {/* MY RATING — mounted only when open */}
        {tab === "rating" && <RatingCard />}

      </main>
    </div>
  );
}
